import { useState } from 'react'
import { useMenuKillCandidates } from '../hooks/useMenuKillCandidates.js'
import { formatMoney } from '../lib/format.js'
import MenuKillDetailModal from './MenuKillDetailModal.jsx'

// Menu kill candidates — items that barely sell over the last 90 days.
//
// Renders three states:
//   1. Loading — skeleton-ish placeholder
//   2. No candidates — green "menu is tight" reassurance
//   3. Candidates — list of dogs with the data to defend the call
//
// Heuristic and tenant scoping live in vw_menu_kill_candidates.
// This component just visualises what the view returns.
//
// briefByItem (optional) — map of item_id → rationale string from the
// AI menu engineering brief. When present, clicking a row opens the
// detail modal with the rationale attached.
export default function MenuKillCandidatesCard({ briefByItem } = {}) {
  const { candidates, survivors, loading, error, refresh } = useMenuKillCandidates()
  const [showAll, setShowAll] = useState(false)
  const [selected, setSelected] = useState(null)

  if (loading) {
    return (
      <section className="mx-4 mt-4 rounded-2xl border border-black/10 bg-white p-4 shadow-sm">
        <div className="text-[11px] font-medium text-black/50">
          Menu Kill Candidates
        </div>
        <div className="mt-2 text-sm text-black/50">Reading 90 days of sales…</div>
      </section>
    )
  }

  if (error) {
    return (
      <section className="mx-4 mt-4 rounded-2xl border border-red-200 bg-red-50/40 p-4 shadow-sm">
        <div className="text-[11px] font-medium text-red-900/70">
          Menu Kill Candidates
        </div>
        <div className="mt-2 text-sm text-red-800">{error}</div>
        <button
          type="button"
          onClick={refresh}
          className="mt-2 rounded-md px-2 py-1 text-xs font-medium text-red-700 hover:bg-red-100"
        >
          Retry
        </button>
      </section>
    )
  }

  // Empty data state — common for fresh accounts
  if (candidates.length === 0 && survivors.length === 0) {
    return null
  }

  // Healthy menu — no kill candidates flagged
  if (candidates.length === 0) {
    return (
      <section className="mx-4 mt-4 rounded-2xl border border-emerald-200 bg-emerald-50/60 p-4 shadow-sm">
        <div className="text-[11px] font-medium text-emerald-900/70">
          Menu Kill Candidates
        </div>
        <div className="mt-2 text-sm text-emerald-900">
          Your menu looks tight — every item moved enough volume in the last 90 days
          to pay rent on its cooler space.
        </div>
      </section>
    )
  }

  const visible = showAll ? candidates : candidates.slice(0, 5)

  return (
    <section className="mx-4 mt-4 rounded-2xl border border-amber-200 bg-amber-50/40 p-4 shadow-sm">
      <div className="flex items-baseline justify-between gap-2">
        <div className="text-[11px] font-medium text-amber-900/70">
          Menu Kill Candidates · {candidates.length}
        </div>
        <button
          type="button"
          onClick={refresh}
          className="rounded-md px-2 py-1 text-xs font-medium text-amber-800 hover:bg-amber-100"
        >
          Refresh
        </button>
      </div>

      <p className="mt-2 text-sm text-black/75">
        {candidates.length} {candidates.length === 1 ? 'item is' : 'items are'} selling
        less than once a week. They&rsquo;re a small share of revenue but take cooler
        space, drive prep complexity, and slow ticket times. Worth a chef conversation.
      </p>

      <ul className="mt-3 divide-y divide-amber-200/60 rounded-xl bg-white/70">
        {visible.map((row) => (
          <li key={`${row.item_id ?? 'noid'}-${row.item_name}`}>
            <button
              type="button"
              data-testid="menu-kill-row"
              aria-label={`Open kill-or-fix detail for ${row.item_name}`}
              onClick={() => setSelected(row)}
              className="flex w-full items-center justify-between gap-3 rounded-xl px-3 py-2.5 text-left hover:bg-amber-100/50 focus:outline-none focus:ring-2 focus:ring-amber-300"
            >
              <div className="min-w-0">
                <div className="truncate text-sm font-medium text-black/90">
                  {row.item_name}
                </div>
                <div className="mt-0.5 text-xs text-black/55">
                  {Number(row.units_90d).toLocaleString()} sold · {formatMoney(row.revenue_90d || 0)} revenue
                </div>
              </div>
              <div className="shrink-0 text-right">
                <div className="text-sm font-medium text-amber-900">
                  {row.units_per_week ?? '0.0'}/wk
                </div>
                <div className="text-[10px] font-medium text-black/45">
                  {row.share_of_revenue_pct != null
                    ? `${row.share_of_revenue_pct}% of rev`
                    : '—'}
                </div>
              </div>
            </button>
          </li>
        ))}
      </ul>

      {candidates.length > 5 && (
        <button
          type="button"
          onClick={() => setShowAll((v) => !v)}
          className="mt-2 rounded-md px-2 py-1 text-xs font-medium text-amber-900 hover:bg-amber-100"
        >
          {showAll ? 'Show top 5 only' : `Show all ${candidates.length} candidates`}
        </button>
      )}

      <div className="mt-3 text-[11px] text-black/50">
        Heuristic: ≤ 13 sales in 90 days <em>and</em> &lt; 0.5% of revenue. Excludes
        rare-but-profitable specials.
      </div>

      <MenuKillDetailModal
        isOpen={!!selected}
        item={selected}
        briefRationale={selected ? (briefByItem?.[selected.item_id] || null) : null}
        onClose={() => setSelected(null)}
      />
    </section>
  )
}
